import posthog from 'posthog-js';
import { create } from 'zustand';

import { API, getRequest, login as apiLogin, postRequest, putRequest } from '@/api/api';
import type { TokensResponse } from '@/api/api.dto';
import { queryClient } from '@/api/queryClient';
import {
  clearCachedSession,
  clearServiceId,
  clearTokens,
  getAccessToken,
  getCachedSession,
  getRefreshToken,
  getServiceId,
  setCachedSession,
  setServiceId,
} from '@/api/tokens.helper.ts';
import type {
  ConfirmInviteData,
  LoginRequest,
  LoginResponse,
  NormalizedSessionUser,
  RegistrationConfirmationResponse,
  RegistrationResponse,
  SessionUserResponse,
  TwoFactorVerifyPayload,
  TwoFactorVerifyResponse,
} from '@/store/auth/auth.dto';
import { useBackgroundTasksStore } from '@/store/background-tasks/background-tasks.store.ts';
import type { TemporaryContract } from '@/store/contracts/contracts.mappers';
import { useImportedTransactionsStore } from '@/store/expenses-store/transactions/imported-transactions.store.ts';
import { usePermissions } from '@/store/permissions/permissions.store';
import { getRolesOrdering, onboardPerson } from '@/store/persons/person.api';
import {
  biometricUnlock,
  disableBiometricLogin,
  isBiometricEnabled,
  refreshStoredToken,
} from '@/utils/biometric';
import { getPrimaryRole, normalizeRoles, UserRole } from '@/utils/roles';

import {
  checkPassword,
  confirmInvite,
  confirmMagicLink,
  confirmTwoFactorMethod as confirmTwoFactorMethodApi,
  disableTwoFactorMethod as disableTwoFactorMethodApi,
  enableTwoFactorMethod as enableTwoFactorMethodApi,
  hardDeleteUser,
  inviteUser,
  inviteUsersBulk,
  sendMagicLink,
  sendTwoFactorCode as sendTwoFactorCodeApi,
  setDefaultTwoFactorMethod as setDefaultTwoFactorMethodApi,
  type TwoFactorChallengeMethod,
  type TwoFactorMethod,
  updatePassword,
  verifyRecoveryCode as verifyRecoveryCodeApi,
  verifyTwoFactorCode as verifyTwoFactorCodeApi,
} from './auth.api';

type TwoFactorMethods = {
  default: string;
  email: boolean;
  totp: boolean;
  sms: boolean;
  call: boolean;
};

export type TwoFactorEnabledResponse = {
  default: string;
  email: boolean;
  totp: boolean;
  sms: boolean;
  call: boolean;
};

type EnableTotpResponse = {
  qr_code: string;
  url: string;
};

type resetPassConfirm = {
  code: string;
  secret: string | undefined;
  password: string;
};

const normalizeSession = async (session: SessionUserResponse): Promise<NormalizedSessionUser> => {
  const serviceEntries = session.services ?? [];
  const existingServiceId = getServiceId();
  let activeServiceId = existingServiceId;

  const validServiceIds = serviceEntries.map((r) => r.service_id);
  const isExistingValid = existingServiceId && validServiceIds.includes(existingServiceId);

  if (!isExistingValid) {
    let chosenId = serviceEntries[0]?.service_id ?? session.id;
    setServiceId(chosenId);

    if (serviceEntries.length > 1) {
      try {
        const ordering = await getRolesOrdering();
        if (ordering?.length) {
          const orderMap = new Map(ordering.map((r) => [r.service_id, r.order]));
          const [firstByOrder] = [...serviceEntries].sort(
            (a, b) =>
              (orderMap.get(a.service_id) ?? Number.MAX_SAFE_INTEGER) -
              (orderMap.get(b.service_id) ?? Number.MAX_SAFE_INTEGER),
          );
          if (firstByOrder?.service_id) {
            chosenId = firstByOrder.service_id;
            setServiceId(chosenId);
          }
        }
      } catch (error) {
        console.error('Failed to fetch roles ordering during session init:', error);
      }
    }

    activeServiceId = chosenId;
  }

  const activeRoleEntries = activeServiceId
    ? serviceEntries.filter((r) => r.service_id === activeServiceId)
    : serviceEntries.slice(0, 1);

  const rawRoles: Array<string | UserRole> = [];
  if (session.role) {
    rawRoles.push(session.role as string | UserRole);
  }
  for (const entry of activeRoleEntries) {
    rawRoles.push(entry.role);
  }

  const userRoles = normalizeRoles(rawRoles);
  const primaryRole = getPrimaryRole(userRoles);

  let basePermissions: string[] = [];
  try {
    basePermissions = await usePermissions.getState().fetchBasePermissions();
  } catch (error) {
    console.error('Failed to fetch base permissions:', error);
  }

  const rights: ReadonlySet<string> = new Set([
    ...basePermissions,
    ...serviceEntries
      .filter((r) => r.service_id === activeServiceId)
      .flatMap((r) => r.rights ?? []),
  ]);

  return {
    ...session,
    services: serviceEntries,
    userRoles,
    primaryRole,
    rights,
  } as NormalizedSessionUser;
};

type AuthState = {
  isLoggedIn: boolean;
  isInitializing: boolean;
  session: NormalizedSessionUser | null;
  twoFactorToken: string | null;
  twoFactorMethods: TwoFactorMethods | null;
  initialize: () => Promise<void>;
  completeOnboarding: () => Promise<void>;
  updateRoleForVessel: (vesselId: string) => Promise<void>;
  login: (login: string, password: string) => Promise<LoginResponse>;
  loginWithBiometric: () => Promise<boolean>;
  verifyTotp: (payload: TwoFactorVerifyPayload) => Promise<boolean>;
  sendTwoFactorCode: (method: TwoFactorChallengeMethod) => Promise<void>;
  verifyTwoFactorCode: (method: TwoFactorChallengeMethod, code: string) => Promise<boolean>;
  verifyRecoveryCode: (code: string) => Promise<boolean>;
  clearTwoFactor: () => void;
  me: () => Promise<SessionUserResponse>;
  logout: () => Promise<void>;
  getTwoFactorEnabled: () => Promise<TwoFactorEnabledResponse>;
  enableTotp: () => Promise<EnableTotpResponse>;
  enableTwoFactorMethod: (method: TwoFactorChallengeMethod) => Promise<void>;
  confirmTwoFactorMethod: (method: TwoFactorMethod, code: string) => Promise<string[]>;
  disableTwoFactorMethod: (method: TwoFactorMethod) => Promise<void>;
  setDefaultTwoFactor: (method: TwoFactorMethod) => Promise<void>;
  resetPassword: (mail: string) => Promise<any>;
  resetPasswordConfirm: ({ code, secret, password }: resetPassConfirm) => Promise<any>;
  changePassword: (login: string, current: string, next: string) => Promise<boolean>;
  register: (login: string, password: string, referralCode?: string) => Promise<string>;
  confirmRegistration: (
    secret: string,
    code: string,
    profile?: { firstName?: string; lastName?: string },
  ) => Promise<void>;
  hardDeleteUser: (userId: string) => Promise<void>;
  completeOAuth: (provider: string, params: Record<string, string>) => Promise<LoginResponse>;
  inviteUser(
    contact: string,
    name: string,
    role: string,
    serviceId: string,
    serviceType: string,
    serviceName?: string,
    roleName?: string,
    event?: { id: string; name: string },
    /** Shown to the invitee with the invitation; the backend caps it at 500 characters. */
    message?: string,
    /** Makes it a temporary hire; people validates the contract. */
    temporary?: TemporaryContract,
  ): Promise<void>;
  inviteUsersBulk(
    invitees: { contact: string; name: string }[],
    role: string,
    serviceId: string,
    serviceType: string,
    serviceName?: string,
    event?: { id: string; name: string },
    message?: string,
    temporary?: TemporaryContract,
  ): Promise<void>;
  confirmInvitation(data: ConfirmInviteData): Promise<void>;
  sendMagicLink: (login: string) => Promise<void>;
  confirmMagicLink: (secret: string) => Promise<LoginResponse>;
};

/**
 * One full page load straight onto /auth, so the next sign-in starts with fresh stores and
 * a fresh PostHog.
 *
 * Not `set({ isLoggedIn: false })` + `reload()`. The state change painted /auth once in the
 * old page, and ProtectedRoute saved the page being left as the post-login target. Then the
 * reload reopened that old URL, and api.ts reloaded onto /auth a second time. `replace` also
 * keeps the signed-out page out of history.
 */
const signOutReload = () => window.location.replace('/auth');

export const useAuth = create<AuthState>((set, get) => ({
  isLoggedIn: false,
  isInitializing: false,
  session: null,
  twoFactorToken: null,
  twoFactorMethods: null,
  // Flips the persisted `onboarded` flag (PATCH /{id}/onboard) and mirrors it into
  // the session so the Layout gate stops redirecting to /onboarding. session.id is
  // the person id. Idempotent: no-op if already onboarded.
  completeOnboarding: async () => {
    const { session } = get();
    if (!session || session.onboarded) return;
    await onboardPerson(session.id);
    set({ session: { ...session, onboarded: true } });
  },
  updateRoleForVessel: async (vesselId: string) => {
    const { session } = get();
    if (!session) return;

    const roleEntries = session.services?.filter((r) => r.service_id === vesselId);
    if (!roleEntries?.length) return;

    const allRoles = roleEntries.map((entry) => entry.role);

    const userRoles = normalizeRoles(allRoles);
    const primaryRole = getPrimaryRole(userRoles);

    let basePermissions: string[] = [];
    try {
      basePermissions = await usePermissions.getState().fetchBasePermissions();
    } catch (error) {
      console.error('Failed to fetch base permissions:', error);
    }

    const rights: ReadonlySet<string> = new Set([
      ...basePermissions,
      ...roleEntries.flatMap((r) => r.rights ?? []),
    ]);

    set({
      session: {
        ...session,
        userRoles,
        primaryRole,
        rights,
      },
    });
  },
  initialize: async () => {
    set({ isInitializing: true });

    // If biometric login is enabled, do NOT silently restore the session from stored
    // tokens. Leave the app locked so the auth screen can require Face ID / Touch ID.
    if (isBiometricEnabled()) {
      set({ isInitializing: false, isLoggedIn: false, session: null });
      return;
    }

    const access = getAccessToken();
    const refresh = getRefreshToken();

    if (access && refresh) {
      apiLogin(access, refresh);
      set({ isLoggedIn: true });

      try {
        const response = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');
        if (response.status === 200) {
          const normalizedSession = await normalizeSession(response.data);
          setCachedSession(normalizedSession);
          set({ session: normalizedSession });
        }
      } catch (error: any) {
        console.error('Failed to load user session:', error);

        // Check if we're offline - if so, try to restore from cache
        const isOffline = !navigator.onLine;
        const isNetworkError =
          error?.code === 'ERR_NETWORK' ||
          error?.message?.includes('Network Error') ||
          error?.message?.includes('Failed to fetch');

        if (isOffline || isNetworkError) {
          // Offline: try to restore session from cache
          const cachedSession = getCachedSession<NormalizedSessionUser>();
          if (cachedSession) {
            console.log('Offline mode: restored session from cache');
            set({ session: cachedSession, isLoggedIn: true });
          } else {
            // No cached session available, but keep logged in state
            // User can still view cached data, just won't have session details
            console.log('Offline mode: no cached session, but keeping auth state');
          }
        } else {
          // Online but auth failed (e.g., 401) - clear tokens
          clearTokens();
          clearCachedSession();
          set({ isLoggedIn: false, session: null });
        }
      }
    }

    set({ isInitializing: false });
  },
  login: async (login: string, password: string): Promise<LoginResponse> => {
    const response = await postRequest<LoginResponse, LoginRequest>(API.AUTH, '/login', {
      login: login.toLowerCase(),
      password,
    });

    if (response.status === 200) {
      const data = response.data;
      if (data.two_factor_enabled && data.two_factor_token) {
        set({
          twoFactorToken: data.two_factor_token,
          twoFactorMethods: (data.two_factor_methods as TwoFactorMethods | undefined) ?? null,
          isLoggedIn: false,
        });
      } else {
        apiLogin(data.tokens.access_token, data.tokens.refresh_token);
        set({ isLoggedIn: true, twoFactorToken: null, twoFactorMethods: null });

        try {
          const sessionResponse = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');
          if (sessionResponse.status === 200) {
            const normalizedSession = await normalizeSession(sessionResponse.data);
            setCachedSession(normalizedSession);
            set({ session: normalizedSession });
            const email = normalizedSession.contacts.find(
              (c) => c.contact_type === 'email',
            )?.contact;
            posthog.capture('login', {
              method: 'email',
              email,
              timestamp: new Date().toISOString(),
            });
          }
        } catch (error) {
          console.error('Failed to load user session after login:', error);
        }
      }
    }

    return response.data;
  },
  loginWithBiometric: async (): Promise<boolean> => {
    const refresh = await biometricUnlock();
    if (!refresh) return false;

    try {
      // Exchange the stored refresh token for a fresh session.
      const res = await postRequest<TokensResponse, { token: string }>(API.AUTH, '/refresh', {
        token: refresh,
      });

      if (res.status !== 200 || !res.data?.access_token) return false;

      apiLogin(res.data.access_token, res.data.refresh_token);
      // The /refresh call above consumed the stored token; persist the rotated one.
      await refreshStoredToken(res.data.refresh_token);
      set({ isLoggedIn: true, twoFactorToken: null, twoFactorMethods: null });

      try {
        const sessionResponse = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');
        if (sessionResponse.status === 200) {
          const normalizedSession = await normalizeSession(sessionResponse.data);
          setCachedSession(normalizedSession);
          set({ session: normalizedSession });
          posthog.capture('login', {
            method: 'biometric',
            timestamp: new Date().toISOString(),
          });
        }
      } catch (error) {
        console.error('Failed to load user session after biometric login:', error);
      }

      return true;
    } catch (error) {
      // Stored refresh token likely expired/revoked — drop it so we fall back to password.
      console.error('Biometric login failed:', error);
      await disableBiometricLogin();
      return false;
    }
  },
  verifyTotp: async (payload: TwoFactorVerifyPayload) => {
    const response = await postRequest<TwoFactorVerifyResponse, TwoFactorVerifyPayload>(
      API.AUTH,
      '/2fa/verify/totp',
      payload,
    );

    if (response.status >= 200 && response.status < 300) {
      const raw = (response.data as any) || {};
      const tokens = raw.tokens ?? raw;

      if (tokens?.access_token && tokens?.refresh_token) {
        apiLogin(tokens.access_token, tokens.refresh_token);
        set({ isLoggedIn: true, twoFactorToken: null, twoFactorMethods: null });

        try {
          const sessionResponse = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');
          if (sessionResponse.status === 200) {
            const normalizedSession = await normalizeSession(sessionResponse.data);
            setCachedSession(normalizedSession);
            set({ session: normalizedSession });
          }
        } catch (error) {
          console.error('Failed to load:', error);
        }

        return true;
      }
    }

    return false;
  },
  sendTwoFactorCode: async (method: TwoFactorChallengeMethod) => {
    const token = get().twoFactorToken;
    if (!token) throw new Error('No two-factor token available');
    await sendTwoFactorCodeApi(method, token);
  },
  verifyTwoFactorCode: async (method: TwoFactorChallengeMethod, code: string) => {
    const token = get().twoFactorToken;
    if (!token) return false;

    const response = await verifyTwoFactorCodeApi(method, token, code);

    if (response.status >= 200 && response.status < 300) {
      const raw = (response.data as any) || {};
      const tokens = raw.tokens ?? raw;

      if (tokens?.access_token && tokens?.refresh_token) {
        apiLogin(tokens.access_token, tokens.refresh_token);
        set({ isLoggedIn: true, twoFactorToken: null, twoFactorMethods: null });

        try {
          const sessionResponse = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');
          if (sessionResponse.status === 200) {
            const normalizedSession = await normalizeSession(sessionResponse.data);
            setCachedSession(normalizedSession);
            set({ session: normalizedSession });
          }
        } catch (error) {
          console.error('Failed to load user session after 2FA:', error);
        }

        return true;
      }
    }

    return false;
  },
  verifyRecoveryCode: async (code: string) => {
    const token = get().twoFactorToken;
    if (!token) return false;

    const response = await verifyRecoveryCodeApi(token, code);

    if (response.status >= 200 && response.status < 300) {
      const raw = (response.data as any) || {};
      const tokens = raw.tokens ?? raw;

      if (tokens?.access_token && tokens?.refresh_token) {
        apiLogin(tokens.access_token, tokens.refresh_token);
        set({ isLoggedIn: true, twoFactorToken: null, twoFactorMethods: null });

        try {
          const sessionResponse = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');
          if (sessionResponse.status === 200) {
            const normalizedSession = await normalizeSession(sessionResponse.data);
            setCachedSession(normalizedSession);
            set({ session: normalizedSession });
          }
        } catch (error) {
          console.error('Failed to load user session after recovery:', error);
        }

        return true;
      }
    }

    return false;
  },
  clearTwoFactor: () => set({ twoFactorToken: null, twoFactorMethods: null }),
  me: async (): Promise<SessionUserResponse> => {
    const response = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');

    if (response.status === 200) {
      const normalizedSession = await normalizeSession(response.data);
      setCachedSession(normalizedSession);
      set({ session: normalizedSession });
    }

    return response.data;
  },
  getTwoFactorEnabled: async (): Promise<TwoFactorEnabledResponse> => {
    const response = await getRequest<TwoFactorEnabledResponse>(API.AUTH, '/2fa/enabled');
    return response.data;
  },
  enableTotp: async (): Promise<EnableTotpResponse> => {
    const res = await postRequest<{ image: string; url: string }, Record<string, never>>(
      API.AUTH,
      '/2fa/enable/totp',
      {},
    );
    const { image, url } = res.data;
    const qr_code = image.startsWith('data:') ? image : `data:image/png;base64,${image}`;
    return { qr_code, url };
  },
  enableTwoFactorMethod: async (method: TwoFactorChallengeMethod) => {
    await enableTwoFactorMethodApi(method);
  },
  confirmTwoFactorMethod: async (method: TwoFactorMethod, code: string) =>
    confirmTwoFactorMethodApi(method, code),
  disableTwoFactorMethod: async (method: TwoFactorMethod) => {
    await disableTwoFactorMethodApi(method);
  },
  setDefaultTwoFactor: async (method: TwoFactorMethod) => {
    await setDefaultTwoFactorMethodApi(method);
  },
  logout: async () => {
    try {
      await postRequest<void, Record<string, never>>(API.AUTH, '/logout', {});
    } finally {
      await disableBiometricLogin();
      clearTokens();
      clearServiceId();
      clearCachedSession();
      if ('caches' in window) {
        caches.delete('api-cache');
        caches.delete('capacitor-api-cache');
      }
      useBackgroundTasksStore.getState().abortAll();
      useImportedTransactionsStore.getState().clearExtractedTransactions();
      sessionStorage.clear();
      // End the current PostHog identity + recording session so the next login
      // (e.g. QA cycling through multiple accounts) gets a cleanly separated,
      // correctly-attributed session replay. The page load below re-inits PostHog,
      // which re-registers the `environment` super property reset() clears.
      posthog.reset();
      signOutReload();
    }
  },
  resetPassword: async (mail: string) => {
    // The login travels in the query string here (unlike /register and /login, which
    // carry it in the body), so it has to be percent-encoded: a raw `+` decodes to a
    // space server-side and breaks both plus-addressed emails ("a+tag@mail.com") and
    // phone logins ("+380…"). Lower-cased to match how /register stores it.
    const login = encodeURIComponent(mail.trim().toLowerCase());
    const response = await postRequest(API.AUTH, `/reset-password?login=${login}`, {});

    set({ isLoggedIn: false });

    return response;
  },
  resetPasswordConfirm: async ({ code, secret, password }: resetPassConfirm) => {
    const response = await postRequest(API.AUTH, `/confirm-reset-password`, {
      code,
      secret,
      password,
    });

    set({ isLoggedIn: false });

    return response.data;
  },
  // Auth signs every session out on a password change, this one included. Signing straight
  // back in with the new password keeps the user where they are; an account with 2FA can't
  // do that without a code, so it resolves false and the caller sends the user to sign in.
  changePassword: async (login: string, current: string, next: string) => {
    await checkPassword(login, current);
    await updatePassword(next);

    try {
      const res = await checkPassword(login, next);
      if (res.two_factor_enabled || !res.tokens?.access_token) return false;
      apiLogin(res.tokens.access_token, res.tokens.refresh_token);
      // The token biometric unlock keeps was revoked with the rest.
      await refreshStoredToken(res.tokens.refresh_token);
      return true;
    } catch (error) {
      console.error('Failed to sign back in after the password change:', error);
      return false;
    }
  },
  register: async (login: string, password: string, referralCode?: string) => {
    const res = await postRequest<RegistrationResponse, unknown>(API.AUTH, `/register`, {
      login: login.toLowerCase(),
      password,
      // `referral_code` on dto.CreateUser — the only write path the referral system
      // has. Omitted entirely when the user did not arrive through an invite link.
      ...(referralCode && { referral_code: referralCode }),
    });

    return res.data.confirmation_token;
  },
  confirmRegistration: async (
    secret: string,
    code: string,
    profile?: { firstName?: string; lastName?: string },
  ) => {
    const res = await postRequest<RegistrationConfirmationResponse, unknown>(
      API.AUTH,
      `/confirm-registration`,
      {
        code,
        secret,
      },
    );
    apiLogin(res.data.access_token, res.data.refresh_token);
    set({ isLoggedIn: true, twoFactorToken: null, twoFactorMethods: null });

    try {
      const sessionResponse = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');
      if (sessionResponse.status === 200) {
        const normalizedSession = await normalizeSession(sessionResponse.data);
        setCachedSession(normalizedSession);
        set({ session: normalizedSession });

        // Prefer the names the user typed during sign-up; otherwise derive them from
        // the email local-part (e.g. "c.hale@…" → Caroline / Hale).
        const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
        let firstName = profile?.firstName?.trim() ?? '';
        let lastName = profile?.lastName?.trim() ?? '';

        if (!firstName && !lastName) {
          const emailContact = sessionResponse.data.contacts.find(
            (c) => c.contact_type === 'email',
          );
          const emailPrefix = emailContact?.contact?.split('@')[0];
          if (emailPrefix) {
            if (emailPrefix.includes('.')) {
              const parts = emailPrefix.split('.');
              firstName = cap(parts[0]);
              lastName = cap(parts.slice(1).join('.'));
            } else {
              firstName = cap(emailPrefix);
            }
          }
        }

        if (firstName || lastName) {
          try {
            await putRequest(API.PEOPLE, `/${sessionResponse.data.id}`, {
              first_name: firstName,
              last_name: lastName,
            });
          } catch (error) {
            console.error('Failed to update user names:', error);
          }
        }

        // Assign default CREW_MEMBER role to newly registered user
        // try {
        //   await putRequest<void, any>(API.PEOPLE, '/roles', {
        //     person_id: sessionResponse.data.id,
        //     roles: [UserRole.CREW_MEMBER],
        //     service_id: '019b55ed-1921-7f2c-9053-45872807df39',
        //     service_type: 'vessel',
        //   });
        //   // Refresh session to get the updated roles
        //   const updatedSessionResponse = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');
        //   if (updatedSessionResponse.status === 200) {
        //     set({ session: normalizeSession(updatedSessionResponse.data) });
        //   }
        // } catch (error) {
        //   console.error('Failed to assign default role:', error);
        // }
      }
    } catch (error) {
      console.error('Failed to load user session after registration:', error);
    }
  },
  hardDeleteUser: async (userId: string) => {
    try {
      await hardDeleteUser(userId);
    } catch (error) {
      console.error('Failed to delete user:', error);
      throw error;
    }
    // Tear down biometric like logout() does — otherwise the stale refresh token survives
    // and the /auth auto-prompt loops Face ID forever after the reload below.
    await disableBiometricLogin();
    clearTokens();
    clearServiceId();
    clearCachedSession();
    if ('caches' in window) {
      caches.delete('api-cache');
      caches.delete('capacitor-api-cache');
    }
    signOutReload();
  },
  completeOAuth: async (
    provider: string,
    params: Record<string, string>,
  ): Promise<LoginResponse> => {
    const response = await postRequest<LoginResponse, Record<string, string>>(
      API.AUTH,
      `/oauth/${provider}/complete`,
      params,
    );

    if (response.status === 200) {
      const data = response.data;
      if (data.two_factor_enabled && data.two_factor_token) {
        set({
          twoFactorToken: data.two_factor_token,
          twoFactorMethods: (data.two_factor_methods as TwoFactorMethods | undefined) ?? null,
          isLoggedIn: false,
        });
      } else if (data.tokens?.access_token && data.tokens?.refresh_token) {
        apiLogin(data.tokens.access_token, data.tokens.refresh_token);
        set({ isLoggedIn: true, twoFactorToken: null, twoFactorMethods: null });

        try {
          const sessionResponse = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');
          if (sessionResponse.status === 200) {
            const normalizedSession = await normalizeSession(sessionResponse.data);
            setCachedSession(normalizedSession);
            set({ session: normalizedSession });
            posthog.capture('login', {
              method: 'oauth',
              provider,
              timestamp: new Date().toISOString(),
            });
          }
        } catch (error) {
          console.error('Failed to load user session after OAuth:', error);
        }
      }
    }

    return response.data;
  },

  async inviteUser(
    contact: string,
    name: string,
    role: string,
    serviceId: string,
    serviceType: string,
    serviceName?: string,
    roleName?: string,
    event?: { id: string; name: string },
    message?: string,
    temporary?: TemporaryContract,
  ) {
    await inviteUser(
      contact,
      name,
      role,
      serviceId,
      serviceType,
      serviceName,
      roleName,
      event,
      message,
      temporary,
    );
    await queryClient.invalidateQueries({ queryKey: ['invites-list'] });
  },

  async inviteUsersBulk(
    invitees: { contact: string; name: string }[],
    role: string,
    serviceId: string,
    serviceType: string,
    serviceName?: string,
    event?: { id: string; name: string },
    message?: string,
    temporary?: TemporaryContract,
  ) {
    await inviteUsersBulk(
      invitees,
      role,
      serviceId,
      serviceType,
      serviceName,
      event,
      message,
      temporary,
    );
    // Every list of open invitations reads this key; a new one shows without a reload.
    await queryClient.invalidateQueries({ queryKey: ['invites-list'] });
  },

  async confirmInvitation(data: ConfirmInviteData) {
    const res = await confirmInvite(data);

    apiLogin(res.data.access_token, res.data.refresh_token);
    set({ isLoggedIn: true, twoFactorToken: null, twoFactorMethods: null });

    try {
      const sessionResponse = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');
      if (sessionResponse.status === 200) {
        const normalizedSession = await normalizeSession(sessionResponse.data);
        setCachedSession(normalizedSession);
        set({ session: normalizedSession });
      }
    } catch (error) {
      console.error('Failed to load user session after registration:', error);
    }
  },

  sendMagicLink: async (login: string) => {
    await sendMagicLink(login);
  },

  confirmMagicLink: async (secret: string): Promise<LoginResponse> => {
    const response = await confirmMagicLink(secret);

    if (response.status === 200) {
      const data = response.data;
      if (data.two_factor_enabled && data.two_factor_token) {
        set({
          twoFactorToken: data.two_factor_token,
          twoFactorMethods: (data.two_factor_methods as TwoFactorMethods | undefined) ?? null,
          isLoggedIn: false,
        });
      } else if (data.tokens?.access_token && data.tokens?.refresh_token) {
        apiLogin(data.tokens.access_token, data.tokens.refresh_token);
        set({ isLoggedIn: true, twoFactorToken: null, twoFactorMethods: null });

        try {
          const sessionResponse = await getRequest<SessionUserResponse>(API.PEOPLE, '/me');
          if (sessionResponse.status === 200) {
            const normalizedSession = await normalizeSession(sessionResponse.data);
            setCachedSession(normalizedSession);
            set({ session: normalizedSession });
            const email = normalizedSession.contacts.find(
              (c) => c.contact_type === 'email',
            )?.contact;
            posthog.capture('login', {
              method: 'magic_link',
              email,
              timestamp: new Date().toISOString(),
            });
          }
        } catch (error) {
          console.error('Failed to load user session after magic link:', error);
        }
      }
    }

    return response.data;
  },
}));
